<template>
  <div class="tw-relative">
    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <input
          type="text"
          class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-text-secondary-200 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors tw-cursor-pointer"
          readonly
          :value="displayValue"
          :placeholder="placeholder"
        />
      </PopoverTrigger>
      <PopoverContent class="tw-w-auto tw-p-0 tw-z-[300]" align="start" @click.stop>
        <div class="tw-flex">
          <!-- Presets Sidebar -->
          <div class="tw-flex tw-flex-col tw-border-r tw-border-gray-200">
            <div class="tw-p-3 tw-border-b tw-border-gray-200">
              <h4 class="tw-text-sm tw-font-medium tw-text-gray-700">
                {{ t('common.datepicker.presets') }}
              </h4>
            </div>
            <div class="tw-flex tw-flex-col tw-space-y-1 tw-p-3">
              <button
                v-for="preset in presetRanges"
                :key="preset.label"
                @click="handlePresetClick(preset.range)"
                class="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm tw-rounded-md tw-hover:bg-gray-100 tw-transition-colors tw-whitespace-nowrap"
              >
                {{ preset.label }}
              </button>
            </div>
            <div class="tw-border-t tw-border-gray-200 tw-p-3">
              <button
                @click="handleClear"
                class="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-bg-white tw-hover:bg-gray-50 tw-transition-colors"
              >
                {{ t('common.actions.clear') }}
              </button>
            </div>
          </div>

          <!-- Calendar Section -->
          <div class="tw-p-3">
            <RangeCalendar
              v-model="internalDateRange"
              @update:model-value="handleDateRangeChange"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Header } from '@tanstack/vue-table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RangeCalendar } from '@/components/ui/range-calendar';
import { CalendarDate, getLocalTimeZone } from '@internationalized/date';
import { DateFormatter } from '@internationalized/date';
import {
  endOfMonth,
  endOfYear,
  endOfWeek,
  startOfMonth,
  startOfYear,
  startOfWeek,
  subMonths,
  subYears,
  subWeeks,
  subDays,
} from 'date-fns';

const { t } = useI18n();

interface PresetRange {
  label: string;
  range: [Date, Date];
}

const props = defineProps<{
  header: Header<any, any>;
  value?: { from: string; to: string };
}>();

const emit = defineEmits<{
  'update:filter': [value: { from: string; to: string } | undefined];
}>();

// Track dropdown state
const isOpen = ref(false);

// Date formatter for display
const df = new DateFormatter('de-DE', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

// Internal date range state - this is what the calendar component uses
const internalDateRange = ref<any>({
  start: undefined,
  end: undefined,
});

// Preset ranges
const presetRanges = computed((): PresetRange[] => [
  {
    label: t('common.datepicker.last_week'),
    range: [
      startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
      endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
    ] as [Date, Date],
  },
  {
    label: t('common.datepicker.last_30_days'),
    range: [subDays(new Date(), 30), new Date()] as [Date, Date],
  },
  {
    label: t('common.datepicker.this_month'),
    range: [startOfMonth(new Date()), new Date()] as [Date, Date],
  },
  {
    label: t('common.datepicker.last_month'),
    range: [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))] as [
      Date,
      Date
    ],
  },
  {
    label: t('common.datepicker.this_year'),
    range: [startOfYear(new Date()), new Date()] as [Date, Date],
  },
  {
    label: t('common.datepicker.last_year'),
    range: [startOfYear(subYears(new Date(), 1)), endOfYear(subYears(new Date(), 1))] as [
      Date,
      Date
    ],
  },
]);

// Placeholder text
const placeholder = computed(() => {
  const columnDef = props.header.column.columnDef as any;
  return (
    columnDef.filterPlaceholder ||
    t('common.titles.filter', {
      columnName: columnDef.header,
    })
  );
});

// Display value for the input field
const displayValue = computed(() => {
  if (
    !internalDateRange.value.start ||
    typeof internalDateRange.value.start.toDate !== 'function'
  ) {
    return '';
  }

  const startDate = internalDateRange.value.start.toDate(getLocalTimeZone());
  const startFormatted = df.format(startDate);

  if (!internalDateRange.value.end || typeof internalDateRange.value.end.toDate !== 'function') {
    return startFormatted;
  }

  const endDate = internalDateRange.value.end.toDate(getLocalTimeZone());
  const endFormatted = df.format(endDate);

  return `${startFormatted} - ${endFormatted}`;
});

// Handle preset click
const handlePresetClick = (range: [Date, Date]) => {
  const [startDate, endDate] = range;

  const startCalendar = new CalendarDate(
    startDate.getFullYear(),
    startDate.getMonth() + 1,
    startDate.getDate()
  );

  const endCalendar = new CalendarDate(
    endDate.getFullYear(),
    endDate.getMonth() + 1,
    endDate.getDate()
  );

  internalDateRange.value = {
    start: startCalendar,
    end: endCalendar,
  };

  // Format dates directly without timezone conversion
  const startFormatted = `${startCalendar.year}-${String(startCalendar.month).padStart(
    2,
    '0'
  )}-${String(startCalendar.day).padStart(2, '0')}`;
  const endFormatted = `${endCalendar.year}-${String(endCalendar.month).padStart(2, '0')}-${String(
    endCalendar.day
  ).padStart(2, '0')}`;

  emit('update:filter', {
    from: startFormatted,
    to: endFormatted,
  });

  // Close the dropdown
  isOpen.value = false;
};

// Handle clear
const handleClear = () => {
  internalDateRange.value = {
    start: undefined,
    end: undefined,
  };

  emit('update:filter', undefined);
  isOpen.value = false;
};

// Handle date range change from calendar
const handleDateRangeChange = (newRange: any) => {
  if (!newRange || (!newRange.start && !newRange.end)) {
    emit('update:filter', undefined);
    return;
  }

  if (newRange.start && newRange.end) {
    // Format dates directly from CalendarDate without timezone conversion
    // CalendarDate months are 1-indexed, so we can use them directly
    const startFormatted = `${newRange.start.year}-${String(newRange.start.month).padStart(
      2,
      '0'
    )}-${String(newRange.start.day).padStart(2, '0')}`;
    const endFormatted = `${newRange.end.year}-${String(newRange.end.month).padStart(
      2,
      '0'
    )}-${String(newRange.end.day).padStart(2, '0')}`;

    emit('update:filter', {
      from: startFormatted,
      to: endFormatted,
    });

    // Close the dropdown when both dates are selected
    isOpen.value = false;
  }
};

// Initialize date range from prop value
const initializeFromValue = () => {
  if (props.value && props.value.from && props.value.to) {
    const startDate = new Date(props.value.from);
    const endDate = new Date(props.value.to);

    internalDateRange.value = {
      start: new CalendarDate(
        startDate.getFullYear(),
        startDate.getMonth() + 1,
        startDate.getDate()
      ),
      end: new CalendarDate(endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate()),
    };
  } else {
    internalDateRange.value = {
      start: undefined,
      end: undefined,
    };
  }
};

// Watch for prop value changes
watch(
  () => props.value,
  () => {
    initializeFromValue();
  }
);

// Initialize on mount
onMounted(() => {
  initializeFromValue();
});

// Close dropdown when clicking outside
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;
  const popoverContent = target.closest('[data-radix-popper-content-wrapper]');
  const relativeContainer = target.closest('.tw-relative');

  if (popoverContent || relativeContainer) {
    return;
  }

  isOpen.value = false;
};

// Add click outside listener
watch(isOpen, open => {
  if (open) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});
</script>
